'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Plus, Search, Eye, Edit2, Copy, Trash2, ChevronUp, ChevronDown, ChevronsUpDown, FileText, Leaf, CheckCircle, Clock, Send, XCircle,  } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import ConfirmModal from '@/components/ui/ConfirmModal';
import ToastContainer, { showToast } from '@/components/ui/Toast';
import { Quotation, QuoteStatus } from '@/lib/types';
import { getQuotations, deleteQuotation, upsertQuotation, generateQuoteNumber, generateId } from '@/lib/storage';
import { MOCK_QUOTATIONS } from '@/lib/mockData';
import { formatINR, todayDDMMYYYY, addDaysDDMMYYYY } from '@/lib/utils';

type SortField = 'quoteNumber' | 'clientName' | 'date' | 'validUntil' | 'grandTotal' | 'status';
type SortDir = 'asc' | 'desc';

const STATUS_TABS: { id: string; label: string; value: QuoteStatus | 'All' }[] = [
  { id: 'tab-all', label: 'All', value: 'All' },
  { id: 'tab-draft', label: 'Draft', value: 'Draft' },
  { id: 'tab-sent', label: 'Sent', value: 'Sent' },
  { id: 'tab-approved', label: 'Approved', value: 'Approved' },
  { id: 'tab-rejected', label: 'Rejected', value: 'Rejected' },
];

function parseDateDDMMYYYY(s: string): number {
  const p = s.split('/');
  if (p.length !== 3) return 0;
  return new Date(parseInt(p[2]), parseInt(p[1]) - 1, parseInt(p[0])).getTime();
}

export default function QuotationListClient() {
  const router = useRouter();
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [activeStatus, setActiveStatus] = useState<QuoteStatus | 'All'>('All');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [deleteTarget, setDeleteTarget] = useState<Quotation | null>(null);
  const [loading, setLoading] = useState(true);

  const loadQuotations = useCallback(() => {
    const stored = getQuotations();
    if (stored.length === 0) {
      // Seed with mock data on first load
      MOCK_QUOTATIONS.forEach((q) => upsertQuotation(q));
      setQuotations(MOCK_QUOTATIONS);
    } else {
      setQuotations(stored);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadQuotations();
  }, [loadQuotations]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = quotations
    .filter((q) => {
      if (activeStatus !== 'All' && q.status !== activeStatus) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        return (
          q.quoteNumber.toLowerCase().includes(s) ||
          q.clientName.toLowerCase().includes(s) ||
          q.subject.toLowerCase().includes(s) ||
          q.clientCompany.toLowerCase().includes(s)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'quoteNumber': cmp = a.quoteNumber.localeCompare(b.quoteNumber); break;
        case 'clientName': cmp = a.clientName.localeCompare(b.clientName); break;
        case 'date': cmp = parseDateDDMMYYYY(a.date) - parseDateDDMMYYYY(b.date); break;
        case 'validUntil': cmp = parseDateDDMMYYYY(a.validUntil) - parseDateDDMMYYYY(b.validUntil); break;
        case 'grandTotal': cmp = a.grandTotal - b.grandTotal; break;
        case 'status': cmp = a.status.localeCompare(b.status); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const statusCounts = quotations.reduce<Record<string, number>>((acc, q) => {
    acc[q.status] = (acc[q.status] || 0) + 1;
    acc['All'] = (acc['All'] || 0) + 1;
    return acc;
  }, {});

  const handleDuplicate = (q: Quotation) => {
    const today = todayDDMMYYYY();
    const newQuote: Quotation = {
      ...q,
      id: generateId(),
      quoteNumber: generateQuoteNumber(),
      date: today,
      validUntil: addDaysDDMMYYYY(today, 30),
      status: 'Draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lineItems: q.lineItems.map((li, i) => ({ ...li, id: `li-dup-${Date.now()}-${i}` })),
    };
    upsertQuotation(newQuote);
    loadQuotations();
    showToast(`Quotation duplicated as ${newQuote.quoteNumber}`, 'success');
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteQuotation(deleteTarget.id);
    loadQuotations();
    showToast(`${deleteTarget.quoteNumber} deleted`, 'success');
    setDeleteTarget(null);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronsUpDown size={13} className="text-muted-foreground/50" />;
    return sortDir === 'asc'
      ? <ChevronUp size={13} className="text-primary" />
      : <ChevronDown size={13} className="text-primary" />;
  };

  const isValidUntilExpired = (dateStr: string) => {
    const ts = parseDateDDMMYYYY(dateStr);
    return ts < Date.now();
  };

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="bg-card border-b border-border px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Leaf size={18} className="text-primary" />
              <h1 className="text-xl font-bold text-foreground">Quotations</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Manage GST-compliant landscaping quotations for Terrace Garden India
            </p>
          </div>
          <Link href="/quotation-form" className="btn-primary">
            <Plus size={16} />
            New Quotation
          </Link>
        </div>

        {/* Status Summary Bar */}
        <div className="bg-card border-b border-border px-6 py-3 flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <Clock size={14} className="text-gray-400" />
            <span className="text-muted-foreground">Draft:</span>
            <span className="font-semibold text-foreground">{statusCounts['Draft'] || 0}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Send size={14} className="text-blue-500" />
            <span className="text-muted-foreground">Sent:</span>
            <span className="font-semibold text-foreground">{statusCounts['Sent'] || 0}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle size={14} className="text-primary" />
            <span className="text-muted-foreground">Approved:</span>
            <span className="font-semibold text-foreground">{statusCounts['Approved'] || 0}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <XCircle size={14} className="text-red-500" />
            <span className="text-muted-foreground">Rejected:</span>
            <span className="font-semibold text-foreground">{statusCounts['Rejected'] || 0}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-background px-6 py-4 flex items-center justify-between gap-4 flex-wrap border-b border-border">
          {/* Status Tabs */}
          <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveStatus(tab.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150 flex items-center gap-1.5 ${
                  activeStatus === tab.value
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {tab.label}
                {statusCounts[tab.value] !== undefined && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                    activeStatus === tab.value
                      ? 'bg-white/20 text-white' :'bg-muted text-muted-foreground'
                  }`}>
                    {statusCounts[tab.value] || 0}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by quote no., client, subject..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-input pl-9 py-2"
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto scrollbar-thin px-6 py-4">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`skel-row-${i}`} className="animate-pulse bg-card border border-border rounded-xl h-14 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-4">
                <FileText size={28} className="text-primary" />
              </div>
              <h3 className="text-base font-bold text-foreground mb-1">
                {search || activeStatus !== 'All' ? 'No quotations match your filters' : 'No quotations yet'}
              </h3>
              <p className="text-sm text-muted-foreground mb-5 max-w-xs">
                {search || activeStatus !== 'All' ?'Try adjusting your search or status filter to find what you need.' :'Create your first TGI quotation to get started with client proposals.'}
              </p>
              {!search && activeStatus === 'All' && (
                <Link href="/quotation-form" className="btn-primary">
                  <Plus size={16} />
                  Create First Quotation
                </Link>
              )}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="table-th">
                        <button
                          onClick={() => handleSort('quoteNumber')}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          Quote No. <SortIcon field="quoteNumber" />
                        </button>
                      </th>
                      <th className="table-th">
                        <button
                          onClick={() => handleSort('clientName')}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          Client <SortIcon field="clientName" />
                        </button>
                      </th>
                      <th className="table-th">
                        <button
                          onClick={() => handleSort('date')}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          Date <SortIcon field="date" />
                        </button>
                      </th>
                      <th className="table-th">
                        <button
                          onClick={() => handleSort('validUntil')}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          Valid Until <SortIcon field="validUntil" />
                        </button>
                      </th>
                      <th className="table-th">Subject</th>
                      <th className="table-th">
                        <button
                          onClick={() => handleSort('status')}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          Status <SortIcon field="status" />
                        </button>
                      </th>
                      <th className="table-th">
                        <button
                          onClick={() => handleSort('grandTotal')}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          Grand Total <SortIcon field="grandTotal" />
                        </button>
                      </th>
                      <th className="table-th text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((q, idx) => {
                      const expired = isValidUntilExpired(q.validUntil) && q.status !== 'Approved' && q.status !== 'Rejected';
                      return (
                        <tr
                          key={q.id}
                          className={`border-b border-border last:border-0 transition-colors duration-100 group ${
                            idx % 2 === 0 ? 'bg-card' : 'bg-muted/20'
                          } hover:bg-secondary/40`}
                        >
                          <td className="table-td">
                            <span className="font-mono text-xs font-semibold text-primary">{q.quoteNumber}</span>
                          </td>
                          <td className="table-td">
                            <div>
                              <p className="font-semibold text-foreground text-sm">{q.clientName}</p>
                              {q.clientCompany && (
                                <p className="text-xs text-muted-foreground truncate max-w-[140px]">{q.clientCompany}</p>
                              )}
                            </div>
                          </td>
                          <td className="table-td">
                            <span className="text-sm tabular-nums">{q.date}</span>
                          </td>
                          <td className="table-td">
                            <span className={`text-sm tabular-nums ${expired ? 'text-red-600 font-semibold' : ''}`}>
                              {q.validUntil}
                              {expired && <span className="ml-1 text-xs text-red-500">(Expired)</span>}
                            </span>
                          </td>
                          <td className="table-td max-w-[200px]">
                            <span className="text-sm text-muted-foreground truncate block">{q.subject}</span>
                          </td>
                          <td className="table-td">
                            <StatusBadge status={q.status} />
                          </td>
                          <td className="table-td">
                            <span className="font-semibold tabular-nums text-foreground">{formatINR(q.grandTotal)}</span>
                          </td>
                          <td className="table-td">
                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                              <Link
                                href={`/quotation-form?id=${q.id}&mode=view`}
                                title="View quotation"
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-150"
                              >
                                <Eye size={15} />
                              </Link>
                              <Link
                                href={`/quotation-form?id=${q.id}&mode=edit`}
                                title="Edit quotation"
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-blue-600 hover:bg-blue-50 transition-all duration-150"
                              >
                                <Edit2 size={15} />
                              </Link>
                              <button
                                onClick={() => handleDuplicate(q)}
                                title="Duplicate quotation"
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-amber-600 hover:bg-amber-50 transition-all duration-150"
                              >
                                <Copy size={15} />
                              </button>
                              <button
                                onClick={() => setDeleteTarget(q)}
                                title="Delete quotation — this cannot be undone"
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-all duration-150"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Table Footer */}
              <div className="px-4 py-3 border-t border-border bg-muted/20 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Showing <span className="font-semibold text-foreground">{filtered.length}</span> of{' '}
                  <span className="font-semibold text-foreground">{quotations.length}</span> quotations
                </span>
                <span>
                  Total value:{' '}
                  <span className="font-semibold text-foreground tabular-nums">
                    {formatINR(filtered.reduce((s, q) => s + q.grandTotal, 0))}
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Quotation"
        message={`Are you sure you want to delete ${deleteTarget?.quoteNumber}? This action cannot be undone.`}
        confirmLabel="Delete Quotation"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        variant="danger"
      />
      <ToastContainer />
    </>
  );
}